import { ToggleService } from "./../../common/sidebar/toggle.service";

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  computed,
} from "@angular/core";
import { NgScrollbarModule } from "ngx-scrollbar";
import { MatExpansionModule } from "@angular/material/expansion";
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterModule,
} from "@angular/router";
import { CommonModule, NgClass } from "@angular/common";
import { CustomizerSettingsService } from "../../customizer-settings/customizer-settings.service";
import {
  MenuItem,
  adminMenuItems,
  studentMenuItems,
  collegeAdminMenuItems,
} from "./sidebar-menu";
import { AuthenticationService } from "../../services/auth.service";
import { HasPermissionDirective } from "../../core/directives/has-permission.directive";
import { MatIconModule } from "@angular/material/icon";
import { AuthTenantService } from "../../authentication/auth-tenant.service";
import { MenuStateService } from "../../services/menu.service";
import { FeatureService } from "../../services/feature.service";

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [
    NgScrollbarModule,
    MatExpansionModule,
    CommonModule,
    RouterLinkActive,
    RouterModule,
    RouterLink,
    MatIconModule,
    // NgClass,
    // HasPermissionDirective,
    MatIconModule,
  ],
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  isSidebarToggled = false;
  isToggled = false;
  panelOpenState = false;
  menuItems: MenuItem[] = [];
  currentUserData!: string | null;
  UserTypeData: number | null = null;
  isForm: boolean = false;
  user: any;
  tenant$ = this.tenantService.tenant$;

  private featureService = inject(FeatureService);

  constructor(
    private toggleService: ToggleService,
    public themeService: CustomizerSettingsService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private tenantService: AuthTenantService,
    private authService: AuthenticationService,
    private menuState: MenuStateService,
  ) { }

  ngOnInit(): void {
    this.toggleService.isSidebarToggled$.subscribe((isSidebarToggled: any) => {
      this.isSidebarToggled = isSidebarToggled;
      this.cdRef.markForCheck();
    });

    this.themeService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });

    this.authenticationService.loggedInUser$.subscribe((user) => {
      if (user) {
        this.user = user;

        // Get the correct base menu for the user type
        let rawMenu = this.authenticationService.getMenuForCurrentUser();

        const collegeAdminTypes = [3, 5, 6, 7, 8];
        if (collegeAdminTypes.includes(user.userType)) {
          this.menuItems = this.applyFeatureGating(rawMenu);
        } else {
          this.menuItems = rawMenu;
        }

        this.menuState.setMenu(this.menuItems);
      } else {
        this.menuItems = [];
        this.menuState.setMenu([]);
      }
      this.cdRef.markForCheck();
    });
  }

  private applyFeatureGating(menu: MenuItem[]): MenuItem[] {
    return menu.map((item) => ({
      ...item,
      isLocked: item.feature
        ? this.featureService.isLocked(item.feature)
        : false,
      subItems: item.subItems?.map((sub) => ({
        ...sub,
        isLocked: sub.feature
          ? this.featureService.isLocked(sub.feature)
          : false,

        subItems: sub.subItems
          ? this.applyFeatureGating(sub.subItems)
          : undefined,
      })),
    }));
  }

  handleNavigation(item: MenuItem) {
    if (item.isLocked) {
      this.router.navigate(["/college-admin-dashboard/feature-locked"], {
        queryParams: { feature: item.title },
      });
    } else if (item.routerLink) {
      const isNavigatingToForm =
        item.routerLink === "/student-dashboard/application-form";
      const isCurrentlyOnForm =
        this.router.url === "/student-dashboard/application-form";

      if (isNavigatingToForm) {
        if (!isCurrentlyOnForm) {
          sessionStorage.removeItem("requestedId");
        }
        console.log("new Application");

        this.router.navigateByUrl(item.routerLink, {
          state: {
            newApplication: true,
            fromSideBar: true,
          },
        });
      }
      else {
        sessionStorage.removeItem("requestedId");
        this.router.navigateByUrl(item.routerLink);
      }
    }
  }

  get dashboardRoute(): string {
    switch (this.user.userType) {
      case 1:
        return "/student-dashboard/welcome";
      case 3:
      case 5:
      case 6:
      case 7:
      case 8:
        return "/college-admin-dashboard/welcome";
      case 4:
        return "/faculty-admin-dashboard/welcome";

      case 9:
        return "/hr-admin-dashboard/welcome";

      case 10:
        return "/applicant-admin-dashboard/welcome";
          
      default:
        return "/admin-dashboard/welcome";
    }
  }
  toggle() {
    this.toggleService.toggle();
  }

  navigateByUrl(item: MenuItem) {
    const isNavigatingToForm =
      item.routerLink === "/student-dashboard/application-form";
    const isCurrentlyOnForm =
      this.router.url === "/student-dashboard/application-form";

    if (isNavigatingToForm) {
      if (!isCurrentlyOnForm) {
        sessionStorage.removeItem("requestedId");
      }
      console.log("new Application");

      this.router.navigateByUrl(item.routerLink, {
        state: {
          newApplication: true,
          fromSideBar: true,
        },
      });
    } else {
      sessionStorage.removeItem("requestedId");
      this.router.navigateByUrl(item.routerLink);
    }
  }

  toggleNestedSubMenu(subItem: MenuItem, parentItem: MenuItem) {
    subItem.isExpanded = !subItem.isExpanded;
    parentItem.subItems?.forEach((item) => {
      if (item !== subItem) {
        item.isExpanded = false;
      }
    });
  }

  toggleSubMenu(item: MenuItem) {
    item.isExpanded = !item.isExpanded;
    this.menuItems.forEach((menuItem) => {
      if (menuItem !== item && menuItem.subItems) {
        menuItem.subItems.forEach((subItem) => {
          subItem.isExpanded = false;
        });
      }
    });
  }

  trackByIndex(index: number, _item: MenuItem): number {
    return index;
  }
}

export function hasAccess(
  required: string[] | undefined,
  userPerms: string[],
): boolean {
  // If menu item has no permissions → allow it
  if (!required || required.length === 0) return true;

  // Super admin override
  if (userPerms.includes("ALL_FUNCTIONS")) return true;

  // Read permissions override (READ_xxx)
  const isReadRequired = required.some((r) => r.startsWith("READ_"));
  if (isReadRequired && userPerms.includes("ALL_FUNCTIONS_READ")) return true;

  // User must have at least ONE of the required permissions
  return required.some((r) => userPerms.includes(r));
}

export function filterMenuByPermissions(menu: MenuItem[]): MenuItem[] {
  const user = JSON.parse(sessionStorage.getItem("currentUser") || "{}");

  const userPermissions: string[] = user?.permissions || [];

  return menu
    .filter((item) => hasAccess(item.permissions, userPermissions))
    .map((item) => ({
      ...item,
      subItems: item.subItems
        ? filterMenuByPermissions(item.subItems)
        : undefined,
    }));
}
